<template>
  <div v-can-enable="'custom_domain:write'" class="sender-domains-config">
    <div v-if="hasSenderDomain" class="mt-4">
      <!-- List senderDomains -->
      <el-table :data="senderDomainsTable" class="mt-4 mb-8" style="width: 100%">
        <el-table-column prop="url" label="Domain">
          <template #default="{ row }">
            <pre>{{ row.url }}</pre>
          </template>
        </el-table-column>
        <el-table-column prop="verificationStatus" label="Verification Status" width="200">
          <template #default="{ row }">
            <p v-if="row.verified" class="bg-green-200 text-green-800 font-semibold px-4 py-1 text-xs rounded-xl inline-block">Verified</p>
            <p v-else class="bg-indigo-200 text-indigo-800 font-semibold px-4 py-1 text-xs rounded-xl inline-block">Pending</p>
          </template>
        </el-table-column>
        <el-table-column label="Actions">
          <template #default="{ row }">
            <div class="flex flex-wrap">
              <!-- Delete button -->
              <div class="ml-auto order-2">
                <el-dropdown trigger="click">
                  <div>
                    <el-button type="text" text class="table-dropdown outline-none focus:outline-none">
                      <font-awesome-icon :icon="['fas', 'ellipsis-v']" class="text-brand-brown font-thin text-xs mx-4" />
                    </el-button>
                  </div>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item :disabled="selectedSenderDomain && selectedSenderDomain.id === row.id" icon="el-icon-delete">
                      <el-popconfirm
                        icon-color="#f7b200"
                        @onConfirm="removeSenderDomain(row)"
                        :title="'Are you sure you want to delete ' + row.url + '?'"
                        cancel-button-text="Nevermind"
                        confirm-button-text="Delete domain"
                      >
                        <span slot="reference" class="py-2">Delete Domain</span>
                      </el-popconfirm>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>

              <el-button
                v-if="!row.verified"
                :loading="selectedSenderDomain && selectedSenderDomain.id === row.id"
                icon="el-icon-refresh"
                type="primary"
                size="small"
                @click="openVerifyDialog(row)"
                class="flex-grow-0"
              >
                Verify
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Dialog: Add Sender Domain -->
    <el-dialog class="p-0" custom-class="container max-w-xl rounded-lg px-0 py-0 no-padding" :visible.sync="showSenderAdd" :append-to-body="true">
      <AddSenderDomain :action="addSenderDomain" :close="closeSenderAddDialog" />
    </el-dialog>

    <!-- Dialog: Verify Sender Domain -->
    <el-dialog
      class="p-0"
      custom-class="container max-w-2xl rounded-lg px-0 py-0 no-padding"
      :visible.sync="showSenderVerify"
      @closed="resetSelection"
      :append-to-body="true"
    >
      <VerifySenderDomain :action="addSenderDomain" :senderDomain="selectedSenderDomain" :close="closeSenderVerifyDialog" />
    </el-dialog>

    <!-- Add Sender Domain Button -->
    <div class="mt-4">
      <el-button
        :disabled="!canAddSenderDomain"
        @click="showSenderAdd = true"
        :type="hasSenderDomain ? 'plain' : 'primary'"
        :class="hasSenderDomain ? 'border-brand-gray-blue border-2 px-6 py-2 rounded-lg' : ''"
      >
        <span :class="hasSenderDomain ? 'text-sm text-brand-blue font-semibold' : ''">Add Sender Domain</span>
      </el-button>
      <div v-if="!canAddSenderDomain" class="bg-white border-2 border-brand-yellow text-brand-brown mt-4 p-4 rounded-lg" role="alert">
        <p class="font-bold text-sm">You have reached your maximum allowed Sender Domains ({{ maxSenderDomains }}).</p>
        <p class="text-sm font-medium">
          You can delete unused domains by clicking the vertical ellipsis button on the right of the domain ->
          <span>
            <font-awesome-icon :icon="['fas', 'ellipsis-v']" class="text-brand-brown font-thin text-xs" />
          </span>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import DunningService from '@/api/dunning.service';
import AddSenderDomain from '@/components/Dunning/Config/SenderAdd.vue';
import VerifySenderDomain from '@/components/Dunning/Config/SenderVerify.vue';

export default {
  name: 'SenderDomainsConfig',
  components: {
    AddSenderDomain,
    VerifySenderDomain,
  },
  props: {
    dunningData: {
      type: Object,
      required: true,
    },
    reload: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      maxSenderDomains: 3,
      showSenderAdd: false,
      showSenderVerify: false,
      selectedSenderDomain: null,
    };
  },
  computed: {
    dunning() {
      return this.dunningData; // dunning object passed in from parent
    },
    senderDomains() {
      return this.dunning?.senderDomains || [];
    },
    senderDomainsTable() {
      return this.senderDomains.map((senderDomain, index) => ({
        index,
        id: senderDomain.id,
        url: senderDomain.url,
        verified: senderDomain.dkimVerified && senderDomain.returnPathVerified,
      }));
    },
    canAddSenderDomain() {
      return this.senderDomains.length < this.maxSenderDomains;
    },
    isSenderDomainVerified() {
      return this.senderDomains.some((domain) => domain?.returnPathVerified && domain?.dkimVerified);
    },
    verifiedSenderDomains() {
      return this.senderDomains.filter((domain) => domain?.returnPathVerified && domain?.dkimVerified);
    },
    hasSenderDomain() {
      return this.senderDomains.some((domain) => !!domain?.url);
    },
  },
  methods: {
    senderDomainIsReady(domainId) {
      const domain = this.senderDomains.find((d) => d.id === domainId);
      return domain?.returnPathVerified && domain?.dkimVerified;
    },
    async removeSenderDomain(row) {
      try {
        this.selectedSenderDomain = this.senderDomains[row.index];
        await DunningService.deleteSenderDomain(row.id);
        await this.reload();
        this.$notify({ title: 'Domain Removed', type: 'success' });
      } catch (error) {
        this.displayError(error);
      } finally {
        this.resetSelection();
      }
    },
    async checkSenderDomain(domain) {
      let success = false;
      this.isCheckingSenderDomain = true;
      // check DNS records for dkim verification
      try {
        await DunningService.verifyDkim(domain.id);
        await this.reload();
      } catch (e) {
        this.$message.error('We were unable to check the status of your domain. Please try again later.');
        console.log(e);
      } finally {
        if (this.senderDomainIsReady(domain.id)) {
          this.$message.success('Sender domain successfully verified!');
          success = true;
        }
        this.isCheckingSenderDomain = false;
      }
      return success;
    },
    async openVerifyDialog(row) {
      // Select domain
      this.selectedSenderDomain = this.senderDomains[row.index];

      // Retrieve latest verification status
      const success = await this.checkSenderDomain(this.selectedSenderDomain);

      // Show dialog if not verified
      if (success) {
        this.showSenderVerify = false;
        this.resetSelection();
      } else {
        this.showSenderVerify = true;
      }
    },
    async addSenderDomain(domain) {
      try {
        // await this.action(domain);
        await DunningService.addSenderDomain(domain);
        await this.reload();
        this.$notify({ title: 'Sender Domain Added', type: 'success' });
      } catch (error) {
        this.displayError(error);
      } finally {
        this.resetSelection();
      }
    },
    displayError(error) {
      if (error?.response?.data?.message) {
        this.$notify({ title: `Error: ${error.response.data.message}`, type: 'error' });
      } else {
        this.$notify({ title: 'Error. Try again or contact us via chat for help.', type: 'error' });
      }
    },

    // Sender dialog helpers:
    closeSenderAddDialog() {
      this.showSenderAdd = false;
    },
    closeSenderVerifyDialog() {
      this.showSenderVerify = false;
    },
    resetSelection() {
      this.selectedSenderDomain = {};
    },
  },
};
</script>

<style>
.el-table pre {
  font-size: 12px;
  white-space: normal !important;
  word-wrap: break-word !important;
}
.el-dialog .el-table pre {
  cursor: pointer;
}
.el-dialog .el-table pre:hover {
  color: black;
}
/* Made the vertical ellipsis a button so it is easier to click
   but the usual methods to hide the outline were not working: */
.table-dropdown {
  --tw-ring-opacity: 0 !important;
}
</style>
